// Общие функции справочников для picker карточки (Form1) и отчётов (DocumentsPage).
// Хуки: cardPickerHooks.js (карточка), reportPickerHooks.js (отчёт).

import { fetchSubjectDataOnce } from './subjectDataApi';

/** Id справочника «Субъекты РФ». */
export const HANDBOOK_SUBJECTS_ID = 12;
/** Id справочника «Филиалы ФБУ Рослесозащита». */
export const HANDBOOK_BRANCHES_ID = 15;
/** Id справочника «Список лесничеств» (parent_id филиала может ссылаться сюда). */
export const HANDBOOK_FOREST_LOCATIONS_ID = 36;

/** Справочник по id (число или строка). */
export function handbookById(handbooks, handbookId) {
  if (!handbooks?.length || handbookId == null) return undefined;
  const want = String(handbookId);
  return handbooks.find((h) => String(h.id) === want);
}

/** id субъекта из form1.srf: строка, число или объект справочника { id, name }. */
export function resolveSubjectRefId(srfValue, handbooks) {
  if (srfValue == null || srfValue === '') return null;
  if (typeof srfValue === 'object' && srfValue.id != null) {
    return String(srfValue.id);
  }
  const raw = String(srfValue).trim();
  if (!raw) return null;
  if (/^\d+$/.test(raw)) return raw;
  const hb = handbookById(handbooks, HANDBOOK_SUBJECTS_ID);
  const byName = hb?.references?.find(
    (r) =>
      String(r.name || '').trim() === raw ||
      String(r.name || '').trim().toLowerCase() === raw.toLowerCase()
  );
  return byName ? String(byName.id) : null;
}

export function resolveSubjectName(srfValue, handbooks) {
  const sid = resolveSubjectRefId(srfValue, handbooks);
  if (!sid) {
    if (typeof srfValue === 'string' && srfValue.trim() && !/^\d+$/.test(srfValue.trim())) {
      return srfValue.trim();
    }
    return '';
  }
  const hb = handbookById(handbooks, HANDBOOK_SUBJECTS_ID);
  const ref = hb?.references?.find((r) => String(r.id) === sid);
  return (ref?.name || '').trim();
}

function labelsToPickerOptions(labels) {
  const seen = new Set();
  const out = [];
  for (const label of labels) {
    const t = String(label || '').trim();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push({ value: t, label: t, description: null });
  }
  return out.sort((a, b) => a.label.localeCompare(b.label, 'ru'));
}

/** forest location id → subject id */
function forestLocationToSubjectMap(handbooks) {
  const flHb = handbookById(handbooks, HANDBOOK_FOREST_LOCATIONS_ID);
  const map = new Map();
  if (!flHb?.references?.length) return map;
  for (const r of flHb.references) {
    if (r.id == null || r.parent_id == null) continue;
    map.set(String(r.id), String(r.parent_id));
  }
  return map;
}

/**
 * Филиалы для субъекта из all-guide.
 * parent_id филиала — id субъекта или id лесничества (как в backend subject_branches).
 */
export function branchPickerOptionsForSubject(handbooks, subjectRefId) {
  const sid = resolveSubjectRefId(subjectRefId, handbooks) ?? String(subjectRefId);
  const branchesHb = handbookById(handbooks, HANDBOOK_BRANCHES_ID);
  if (!branchesHb?.references?.length || !sid) return [];

  const flToSubject = forestLocationToSubjectMap(handbooks);
  const seen = new Set();
  const labels = [];

  for (const r of branchesHb.references) {
    const pid = r.parent_id != null ? String(r.parent_id) : '';
    if (!pid) continue;
    const matchesSubject = pid === sid || flToSubject.get(pid) === sid;
    if (!matchesSubject) continue;
    const label = (r.name || '').trim();
    if (!label || seen.has(label)) continue;
    seen.add(label);
    labels.push(label);
  }
  return labelsToPickerOptions(labels);
}

/** Филиалы из subject-data (общий кэш запроса с loadSubjectData). */
export async function fetchBranchPickerOptionsForSubject(subjectId, subjectName) {
  const { ok, data } = await fetchSubjectDataOnce(subjectId, subjectName);
  if (!ok || !data) return [];
  return labelsToPickerOptions(data.branches || []);
}

/** Все филиалы из справочника (вкладка «Отчёт»). */
export function allBranchPickerOptions(handbooks) {
  const branchesHb = handbookById(handbooks, HANDBOOK_BRANCHES_ID);
  if (!branchesHb?.references?.length) return [];
  const labels = branchesHb.references.map((r) => (r.name || '').trim()).filter(Boolean);
  return labelsToPickerOptions(labels);
}
